import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { registerIncidentFormSchema, type RegisterIncidentFormValues } from '@/schemas/attendance';
import { useUI } from '@/hooks/ui';
import { useSidePanelLoading } from '@/hooks/ui/useSidePanelLoading';
import { useCatalogLoader } from '@/hooks/ui/useCatalogLoader';
import { locationService } from '@/api/domains/location';
import type { LC_LocationOut } from '@/api/domains/location';
import { incidentErrorTypeService, markingEventService } from '@/api/domains/catalog';
import type { IncidentErrorType, MarkingEvent } from '@/api/domains/catalog';
import { employeeIncidentService } from '@/api/domains/employeeIncident';
import { serverTimeService } from '@/api/domains/serverTime';
import { useSelector } from 'react-redux';
import type { RootState } from '@/store';
import { injectValidationErrors } from '@/utils/formValidationErrors';
import { formatDateToYYYYMMDD, toHHmmss, TIME_INPUT_REGEX } from '@/utils';


export interface IncidentRecord {
  id?: string;
  checkInDate?: string;
  checkInTime?: string;
  locationId?: string;
  incidentTypeId?: string;
  eventId?: string;
  comment?: string;
  [key: string]: unknown;
}

interface UseRegisterIncidentFormParams {
  onSubmit?: (data: RegisterIncidentFormValues) => void;
  readOnly?: boolean;
  record?: IncidentRecord;
}

export function useRegisterIncidentForm({
  onSubmit,
  readOnly,
  record,
}: UseRegisterIncidentFormParams) {
  const { showNotification, closeSidePanel } = useUI();
  const { setLoading } = useSidePanelLoading();
  const user = useSelector((state: RootState) => state.auth.user);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Load catalogs using useCatalogLoader
  const employeeId = user?.idEmpleado ?? 0;
  const locationsCatalog = useCatalogLoader<LC_LocationOut>(
    (signal) => locationService.getEmployeeEnabledLocations(employeeId, signal),
    (location) => ({
      value: String(location.ubicationId),
      label: location.ubicationName || '',
    }),
    [employeeId],
    !employeeId,
  );

  const incidentTypesCatalog = useCatalogLoader<IncidentErrorType>(
    (signal) => incidentErrorTypeService.getIncidentErrorTypes(signal),
    (type) => ({
      value: String(type.id),
      label: type.description,
    }),
    [],
  );

  const eventTypesCatalog = useCatalogLoader<MarkingEvent>(
    (signal) => markingEventService.getMarkingEvents(signal),
    (event) => ({
      value: String(event.id),
      label: event.description,
    }),
    [],
  );

  const locations = locationsCatalog.options;
  const incidentTypes = incidentTypesCatalog.options;
  const eventTypes = eventTypesCatalog.options;

  const [serverBaseMs, setServerBaseMs] = useState<number | null>(null);
  const [clientBaseMs, setClientBaseMs] = useState<number | null>(null);

  const now = useMemo(() => new Date(), []);
  const fallbackDate = useMemo(() => now.toISOString().slice(0, 10), [now]); // YYYY-MM-DD

  // Load server time for maxDate calculation only
  useEffect(() => {
    let mounted = true;
    void (async () => {
      const response = await serverTimeService.get();
      if (response.success && response.data?.localDateTime) {
        const serverNow = new Date(response.data.localDateTime);
        if (mounted) {
          setServerBaseMs(serverNow.getTime());
          setClientBaseMs(Date.now());
        }
      }
    })();
    return () => { mounted = false; };
  }, []);

  const effectiveReadOnly = !!readOnly;

  const [locationValue, setLocationValue] = useState<string | undefined>(undefined);
  const [incidentTypeValue, setIncidentTypeValue] = useState<string | undefined>(undefined);
  const [eventValue, setEventValue] = useState<string | undefined>(undefined);
  const [commentValue, setCommentValue] = useState<string>('');
  const [checkInDateValue, setCheckInDateValue] = useState<string>(
    record?.checkInDate ?? fallbackDate,
  );
  const [checkInTimeValue, setCheckInTimeValue] = useState<string>(() => {
    if (record?.checkInTime) return toHHmmss(record.checkInTime);
    return '';
  });

  useEffect(() => {
    if (record?.locationId) {
      setLocationValue(String(record.locationId));
    } else if (!locationValue && locations.length) {
      setLocationValue(locations[0].value);
    }
  }, [record?.locationId, locations, locationValue]);

  useEffect(() => {
    if (record?.incidentTypeId) {
      setIncidentTypeValue(String(record.incidentTypeId));
    } else if (!incidentTypeValue && incidentTypes.length) {
      setIncidentTypeValue(incidentTypes[0].value);
    }
  }, [record?.incidentTypeId, incidentTypes, incidentTypeValue]);

  useEffect(() => {
    if (record?.eventId) {
      setEventValue(String(record.eventId));
    } else if (!eventValue && eventTypes.length) {
      setEventValue(eventTypes[0].value);
    }
  }, [record?.eventId, eventTypes, eventValue]);

  useEffect(() => {
    if (record?.comment) {
      setCommentValue(record.comment);
    }
  }, [record?.comment]);


  const checkInDate = checkInDateValue;
  const checkInTime = checkInTimeValue;

  // Compute maxDate for DatePicker (today in YYYY-MM-DD format)
  const maxDate = useMemo(() => {
    if (serverBaseMs && clientBaseMs) {
      const current = new Date(serverBaseMs + (Date.now() - clientBaseMs));
      return formatDateToYYYYMMDD(current);
    }
    return formatDateToYYYYMMDD(new Date());
  }, [serverBaseMs, clientBaseMs]);

  // Date change handler
  const handleDateChange = useCallback((newDate: string) => {
    setCheckInDateValue(newDate);
  }, []);

  // Time change handler with validation
  // Uses TIME_INPUT_REGEX to allow partial input while typing (e.g., "09", "09:3", "09:30:0")
  // Final validation uses TIME_STRICT_REGEX in the schema to ensure complete valid time
  const handleTimeChange = useCallback((newTime: string) => {
    if (TIME_INPUT_REGEX.test(newTime) || newTime === '') {
      setCheckInTimeValue(newTime);
    }
  }, []);

  const {
    handleSubmit: rhfHandleSubmit,
    setValue,
    setError,
    register,
    unregister,
    formState,
  } = useForm<RegisterIncidentFormValues>({
    resolver: zodResolver(registerIncidentFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      incidentTypeId: incidentTypeValue ?? '',
      locationId: locationValue ?? '',
      eventId: eventValue ?? '',
      checkInDate,
      checkInTime,
      comment: commentValue,
    },
  });

  // Field mapping for backend validation errors
  const fieldMapping = useMemo(() => ({
    incidentTypeId: { formInstance: { setError } },
    locationId: { formInstance: { setError } },
    eventId: { formInstance: { setError } },
    checkInDate: { formInstance: { setError } },
    checkInTime: { formInstance: { setError } },
    comment: { formInstance: { setError } },
  }), [setError]);

  useEffect(() => {
    register('incidentTypeId');
    register('locationId');
    register('eventId');
    register('checkInDate');
    register('checkInTime');
    register('comment');
    return () => {
      unregister('incidentTypeId');
      unregister('locationId');
      unregister('eventId');
      unregister('checkInDate');
      unregister('checkInTime');
      unregister('comment');
    };
  }, [register, unregister]);

  useEffect(() => {
    setValue('checkInDate', checkInDate);
    setValue('checkInTime', checkInTime);
  }, [checkInDate, checkInTime, setValue]);

  useEffect(() => {
    setValue('incidentTypeId', incidentTypeValue ?? '');
  }, [incidentTypeValue, setValue]);

  useEffect(() => {
    setValue('locationId', locationValue ?? '');
  }, [locationValue, setValue]);

  useEffect(() => {
    setValue('eventId', eventValue ?? '');
  }, [eventValue, setValue]);

  useEffect(() => {
    setValue('comment', commentValue);
  }, [commentValue, setValue]);

  const fieldsRegisteredRef = useRef(false);
  useEffect(() => {
    fieldsRegisteredRef.current = true;
    return () => { fieldsRegisteredRef.current = false; };
  }, []);

  const isReady = useMemo(() => {
    const hasDate = typeof checkInDate === 'string' && checkInDate.length > 0;
    const hasTime = typeof checkInTime === 'string' && checkInTime.length > 0;
    const hasIncidentType = typeof incidentTypeValue === 'string' && incidentTypeValue.length > 0;
    const hasLocation = typeof locationValue === 'string' && locationValue.length > 0;
    const hasEvent = typeof eventValue === 'string' && eventValue.length > 0;
    const hasComment = typeof commentValue === 'string' && commentValue.trim().length > 0;
    return (
      fieldsRegisteredRef.current && hasDate && hasTime &&
      hasIncidentType && hasLocation && hasEvent && hasComment
    );
  }, [checkInDate, checkInTime, incidentTypeValue, locationValue, eventValue, commentValue]);

  // Track if all catalogs have loaded
  const areCatalogsLoaded = (
    locationsCatalog.isLoaded && incidentTypesCatalog.isLoaded && eventTypesCatalog.isLoaded
  );
  
  // Report loading state to SidePanel (based on catalog loading)
  useEffect(() => {
    setLoading(!areCatalogsLoaded);
  }, [areCatalogsLoaded, setLoading]);

  const onValid = useCallback(
    async (data: RegisterIncidentFormValues) => {
      if (readOnly) return;
      setIsSubmitting(true);

      const incidentTypeId = Number(data.incidentTypeId);
      const locationId = Number(data.locationId);
      const eventId = Number(data.eventId);
      const submittingEmployeeId = user?.idEmpleado ?? 0;
      const companyId = user?.idEmpresa ?? 0;

      if (!Number.isFinite(incidentTypeId) || incidentTypeId <= 0) {
        showNotification('error', 'Tipo de incidente inválido', 5000);
        setIsSubmitting(false);
        return;
      }

      if (!Number.isFinite(locationId) || locationId <= 0) {
        showNotification('error', 'Ubicación inválida', 5000);
        setIsSubmitting(false);
        return;
      }

      if (!Number.isFinite(eventId) || eventId <= 0) {
        showNotification('error', 'Evento inválido', 5000);
        setIsSubmitting(false);
        return;
      }

      if (submittingEmployeeId <= 0 || companyId <= 0) {
        showNotification('error', 'Usuario no autorizado', 5000);
        setIsSubmitting(false);
        return;
      }

      const response = await employeeIncidentService.register({
        incidentTypeId,
        locationId,
        eventId,
        checkInDate: `${data.checkInDate}T${data.checkInTime}`,
        checkInTime: data.checkInTime,
        comment: data.comment,
        employeeId: submittingEmployeeId,
        companyId,
      });

      if (response.success && response.data) {
        const message = response.data.message || 'Incidencia registrada correctamente';
        showNotification('success', message, 5000);
        onSubmit?.(data);
        closeSidePanel?.();
      } else if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
        // Handle backend validation errors (flexible: works with any error code)
        injectValidationErrors(
          response.error.validationErrors,
          fieldMapping,
        );
        showNotification('error', 'Corrige los errores de validación', 4000);
      } else {
        const msg =
          response.error?.description
          || response.data?.message
          || 'No se pudo registrar la incidencia';
        showNotification('error', msg, 5000);
      }
      setIsSubmitting(false);
    },
    [
      showNotification,
      onSubmit,
      closeSidePanel,
      readOnly,
      user,
      fieldMapping,
    ],
  );

  const onInvalid = useCallback((): void => {
  }, []);

  const handleSubmit = useCallback(
    (event?: React.FormEvent<HTMLFormElement>) => {
      if (event && typeof event.preventDefault === 'function') {
        event.preventDefault();
        event.stopPropagation();
      }
      if (effectiveReadOnly) return;
      const wrappedValid = (data: RegisterIncidentFormValues) => { void onValid(data); };
      const wrappedInvalid = () => { onInvalid(); };
      const runner = rhfHandleSubmit(wrappedValid, wrappedInvalid);
      void runner(event as any);
    },
    [rhfHandleSubmit, onValid, onInvalid, effectiveReadOnly],
  );

  return {
    checkInDate,
    checkInTime,
    maxDate,
    handleDateChange,
    handleTimeChange,
    locations,
    locationValue,
    setLocationValue,
    incidentTypes,
    incidentTypeValue,
    setIncidentTypeValue,
    eventTypes,
    eventValue,
    setEventValue,
    commentValue,
    setCommentValue,
    effectiveReadOnly,
    isSubmitting,
    isReady,
    handleSubmit,
    errors: formState.errors,
    isSubmitted: formState.isSubmitted,
  } as const;
}
